import {
  useCurriculum,
  useSubject,
  useSubjectPlan,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useEffect, useReducer } from "react";
import { useParams } from "react-router-dom";

import { DndKitContextDefaults } from "@/contexts/DndKitContextDefaults/DndKitContextDefaults";

import { useHandleTableEvents } from "@/hooks/useHandleTableEvents/useHandleTableEvents";

import { LoadingPage } from "@/pages/LoadingPage/LoadingPage";

import {
  initialState,
  unitTableReducer,
} from "@/states/unitTableReducer/unitTableReducer";

import { mapApiDataToTableReducer } from "@/utils/mapApiDataToTableReducer/mapApiDataToTableReducer";

import { EditSubjectPlan } from "./EditSubjectPlan";

export const EditSubjectPlanContainer = () => {
  const { curriculum_id, subject_id } = useParams();

  if (!curriculum_id || !subject_id) {
    throw new Error("curriculum_id and subject_id are required");
  }

  const {
    actions: { refresh, update },
    data: subjectPlan,
    error: subjectPlanError,
    isLoading: isSubjectPlanLoading,
  } = useSubjectPlan({
    curriculumId: curriculum_id,
    subjectId: subject_id,
  });

  const {
    actions: { update: updateSubject },
    data: subject,
    error: subjectError,
    isLoading: isSubjectLoading,
  } = useSubject({ subjectId: subject_id, curriculumId: curriculum_id });

  const {
    data: curriculum,
    error: curriculumError,
    isLoading: isLoadingCurriculum,
  } = useCurriculum({
    id: curriculum_id,
  });

  const [state, dispatch] = useReducer(unitTableReducer, initialState);

  useEffect(() => {
    // we don't care about the loading state of anything but the subject plan here
    if (state.present.isLoading && !isSubjectPlanLoading && subjectPlan) {
      dispatch({
        type: "INITIALISE_TABLE",
        payload: {
          ...mapApiDataToTableReducer(subjectPlan),
          isLoading: false,
        },
      });
    }
  }, [isSubjectPlanLoading, state.present.isLoading, subjectPlan]);

  const {
    handleAddCustomUnit,
    handleBatchSettingsUpdate,
    handleDragEnd,
    handleRedo,
    handleRemoveUnit,
    handleResetLoadingFlag,
    handleSaveAdaptationNote,
    handleUndo,
    handleUpdateCustomUnit,
  } = useHandleTableEvents({
    state: state.present,
    dispatch,
  });

  const isLoading =
    isSubjectPlanLoading || isSubjectLoading || isLoadingCurriculum;

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <DndKitContextDefaults onDragEnd={handleDragEnd}>
      <EditSubjectPlan
        canRedo={state.future.length > 0}
        canUndo={state.past.length > 0}
        clientVersion={state?.present.version}
        curriculum={curriculum}
        error={subjectPlanError || subjectError || curriculumError}
        handleRefreshSubjectPlan={async () => {
          // we need to reset the loading flag to allow for SWR and the reducer to sync up again.
          handleResetLoadingFlag();
          await refresh.apply();
        }}
        isLoading={isLoading}
        isUpdating={update.isLoading}
        resources={state.present.resources}
        subject={subject || undefined}
        subjectPlanId={subjectPlan?.plan.id}
        tableData={{
          columns: state.present.columns,
          rows: state.present.rows,
          cells: state.present.cells,
        }}
        onAddCustomUnit={handleAddCustomUnit}
        onBatchSettingsUpdate={handleBatchSettingsUpdate}
        onRedo={handleRedo}
        onRemoveUnit={handleRemoveUnit}
        onSaveSubjectPlan={update.apply}
        onUndo={handleUndo}
        onUpdateCustomUnit={handleUpdateCustomUnit}
        onUpdateSubjectStatus={updateSubject.apply}
        onUpsertAdaptationNote={handleSaveAdaptationNote}
      />
    </DndKitContextDefaults>
  );
};
